import { Container, Platform, Stream, T_Platform } from '@cgs/wge';
import { IStorageService, STORAGE_SERVICE_TOKEN } from '@cgs/shared';
export const T_IAppSettings = Symbol('IAppSettings');

const enum AppSettingsKey {
  NotificationsKey = 'notifications',
  SoundsKey = 'sounds',
  MusicKey = 'music',
  SoundFXKey = 'soundfx',
  DontStopKey = 'DontStopKey',
  SharingKey = 'sharing',
  JackpotKey = 'jackpot',
  QuickSpinKey = 'quickspin',
}

interface AppSettingChangeEvent<T> {
  key: string;
  value: T;
}

export interface IAppSettings {
  hasSound: boolean;
  sounds: number;
  soundMusic: boolean;
  soundFX: boolean;
  notifications: boolean;
  dontStopKey: boolean;
  sharing: boolean;
  jackpot: boolean;
  quickSpin: boolean;
  onChange: Stream<AppSettingChangeEvent<any>>;
}

export class AppSettings implements IAppSettings {
  public readonly onChange: Stream<AppSettingChangeEvent<any>> = new Stream();

  private readonly platform: Platform;
  private readonly storageService: IStorageService;

  private _sounds: number = 1;
  private _soundMusic: boolean = true;
  private _soundFX: boolean = true;

  constructor(container: Container) {
    this.platform = container.resolve<Platform>(T_Platform);
    this.storageService = container.resolve<IStorageService>(STORAGE_SERVICE_TOKEN);

    this.sounds = this.getSetting(AppSettingsKey.SoundsKey, 1);
    this.soundFX = this.getSetting(AppSettingsKey.SoundFXKey, true);
    this.soundMusic = this.getSetting(AppSettingsKey.MusicKey, true);
  }

  get quickSpin(): boolean {
    return this.getSetting(AppSettingsKey.QuickSpinKey, false);
  }

  set quickSpin(value: boolean) {
    this.storageService.setItem(AppSettingsKey.QuickSpinKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.QuickSpinKey, value });
  }

  private setSound(value: number): void {
    this.platform.audioSystem.audioDevice.globalVolume = Math.min(Math.max(value, 0), 1);
  }

  private getSetting<T>(itemKey: string, defaultValue: T): T {
    const value = this.storageService.getItem(itemKey);

    if (value === null) {
      return defaultValue;
    }

    if (typeof defaultValue === 'number') {
      const num = parseFloat(value);
      return (isNaN(num) ? defaultValue : num) as T;
    } else if (typeof defaultValue === 'boolean') {
      return (value === 'true' || value === '1') as T;
    } else {
      return value as T;
    }
  }

  public get notifications(): boolean {
    return this.getSetting(AppSettingsKey.NotificationsKey, true);
  }

  public get hasSound(): boolean {
    return (this.soundFX || this.soundMusic) && this.sounds > 0;
  }

  public set hasSound(value: boolean) {
    this.soundMusic = value;
    this.soundFX = value;
    if (value) {
      this.sounds = this.sounds || 1;
    }
  }

  public get soundMusic(): boolean {
    return this._soundMusic;
  }

  public set soundMusic(value: boolean) {
    this._soundMusic = value;
    this.storageService.setItem(AppSettingsKey.MusicKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.MusicKey, value });
  }

  public get soundFX(): boolean {
    return this._soundFX;
  }

  public set soundFX(value: boolean) {
    this._soundFX = value;
    this.storageService.setItem(AppSettingsKey.SoundFXKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.SoundFXKey, value });
  }

  public set dontStopKey(value: boolean) {
    this.storageService.setItem(AppSettingsKey.DontStopKey, value.toString());
  }

  public get dontStopKey(): boolean {
    return this.getSetting(AppSettingsKey.DontStopKey, false);
  }

  public get sounds(): number {
    return this._sounds;
  }

  public set sounds(value: number) {
    this.storageService.setItem(AppSettingsKey.SoundsKey, value.toString());
    this._sounds = value;
    this.setSound(value);
    this.onChange.emit({ key: AppSettingsKey.SoundsKey, value });
  }

  get sharing(): boolean {
    return this.getSetting(AppSettingsKey.SharingKey, true);
  }

  set sharing(value: boolean) {
    this.storageService.setItem(AppSettingsKey.SharingKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.SharingKey, value });
  }

  get jackpot(): boolean {
    return this.getSetting(AppSettingsKey.JackpotKey, true);
  }

  set jackpot(value: boolean) {
    this.storageService.setItem(AppSettingsKey.JackpotKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.JackpotKey, value });
  }

  set notifications(value: boolean) {
    this.storageService.setItem(AppSettingsKey.NotificationsKey, value.toString());
    this.onChange.emit({ key: AppSettingsKey.NotificationsKey, value });
  }
}
